import { nextTick, watch, camelize, ref } from 'vue'
import type { ComponentOptions } from 'vue'
import { setupComponentStateDisplay } from './DebugComponentStateDisplay'
import { useStorage } from '@vueuse/core'

const DEBUG_LABELS_STORAGE_KEY = 'debug.showDebugLabels'

export const showDebugLabels = useStorage<boolean>(DEBUG_LABELS_STORAGE_KEY, Boolean(import.meta.dev))
export const DebugLabelClassName = 'debug-label'
export const debugLabelsInstalled = ref(false)
function getLabelPosition(el: HTMLElement): `${number}-${number}` {
  const rect = el.getBoundingClientRect()
  const topRounded = Math.ceil(rect.top / 10) * 10
  const rightRounded = Math.ceil(rect.right / 10) * 10
  return `${topRounded}-${rightRounded}`
}

export function createDebugComponentNamesMixin() {
  const LabelsPositionMap = new Map<string, HTMLElement[]>()

  console.log(`%c Setup Component DebugLabels`, 'color: #FFC66DFF;background: #0000007F; font-weight: bold;')
  console.log(LabelsPositionMap)
  debugLabelsInstalled.value = true

  watch(showDebugLabels, (value, oldValue) => {
    if (value === oldValue) return
    const newDisplay = value ? 'block' : 'none'
    for (const [_, labels] of LabelsPositionMap.entries()) {
      for (const label of labels) {
        label.style.display = newDisplay
      }
    }
  })

  /**
   * Attaches labels to components and in HTML tags
   *
   * Can be clicked to log component hierarchy and state.
   * Hover with Shift key pressed to display component state at bottom of screen.
   * For `<script setup>` a `defineOptions()` is required to get this mixin merged.
   */
  const DebugComponentNamesMixin: ComponentOptions = {
    data() {
      return {
        __debug_label: null as HTMLElement | null,
        __debug_label_position: '',
      }
    },
    mounted() {
      const componentName = this.$options.name
      let componentEl = this.$el
      const componentPath = this.$options.__file

      if (!componentName || !componentEl) return

      if (!(componentEl instanceof HTMLElement)) {
        componentEl = componentEl.nextElementSibling || componentEl.previousElementSibling
        if (!(componentEl instanceof HTMLElement)) {
          return
        }
      }
      // Add the component name to the HTML tag
      componentEl.dataset[camelize(componentName)] = ''

      // Only include components in these locations
      const includePaths = [/\/components\//, /\/pages\//, /\/layouts\//]
      if (!includePaths.some((rx) => rx.test(componentPath))) {
        return
      }
      // Create the label element but keep reference so we can hide/show it dynamically
      const label = document.createElement('small')
      this.$data.__debug_label = label
      label.textContent = componentName
      label.className = DebugLabelClassName
      label.style.display = showDebugLabels.value ? 'block' : 'none'
      const computed = getComputedStyle(componentEl)
      if (computed.position != 'relative' && computed.position != 'absolute') {
        componentEl.style.position = 'relative'
      }
      componentEl.appendChild(label)

      // Setup component state display (Shift+Hover functionality)
      setupComponentStateDisplay(this, label)

      // Neues Verhalten: Beim Klick den Komponentenbaum bis Root loggen
      label.onclick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        // Komponentenhierarchie bis Root
        const hierarchy: string[] = []
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let current: any = this
        while (current) {
          const name = current.$options?.name || '(anonymous)'
          hierarchy.push(name)
          current = current.$parent
        }
        const hierarchyPreview = hierarchy.slice(1, 4).join(' < ')
        const treeText = hierarchy.reverse().join(' > ')

        console.log(`%c ${componentName} `, 'background:#4FC08D;color:white;font-weight:bold;', `< ${hierarchyPreview}`)
        console.log(this.$)
        console.groupCollapsed('%c Component tree: ', 'color:white;background:grey;font-weight:bold;')
        console.log(treeText)
        console.groupEnd()

        if (e.shiftKey) {
          navigator.clipboard.writeText(componentName)
        } else {
          navigator.clipboard.writeText(componentPath)
        }
        // Notify.info(`Copied! <br><b>${componentPath}</b>`)
      }

      label.oncontextmenu = (e) => {
        e.preventDefault()
        e.stopPropagation()
        label.remove()
      }

      nextTick(() => {
        let top = 0
        const positionkey = getLabelPosition(componentEl)
        this.$data.__debug_label_position = positionkey
        const labelsOnPosition = LabelsPositionMap.get(positionkey)
        if (labelsOnPosition) {
          top = labelsOnPosition.length * 10
          labelsOnPosition.push(label)
        } else {
          const arr = [label]
          LabelsPositionMap.set(positionkey, arr)
        }
        label.style.top = `${top}px`
      })
    },
    unmounted() {
      const arr = LabelsPositionMap.get(this.$data.__debug_label_position)
      if (arr) {
        const index = arr.indexOf(this.$data.__debug_label)
        if (index === -1) return
        arr.splice(index, 1)
      }
    },
  }

  return DebugComponentNamesMixin
}
